import { defineStore, storeToRefs } from 'pinia';
import { useSelectionItemStore } from '@organisms/offer/selection-items/stores/useSelectionItemStore';
import { computed, ref, watch } from 'vue';
import type { SelectionItem } from '@models/selectionItem.ts';
import type { GoogleMap, GoogleMapLatLngBounds, GoogleMapLatLngLiteral } from '@global-types/googleMap.ts';
import { useCustomMarkerStore } from '@organisms/offer/selection-items/stores/useCustomMarkerStore';

interface Marker extends GoogleMapLatLngLiteral {
  id: number,
  title: string | number,
}

export const useSelectionItemMapStore = defineStore('selectionItemMap', () => {
  const { activeSelectionItems } = storeToRefs(useSelectionItemStore());
  const { savedDealUdurMarker } = storeToRefs(useCustomMarkerStore());

  const map = ref<GoogleMap | null>(null);

  const markers = computed<Marker[]>(
    () => (activeSelectionItems.value as SelectionItem[]).map(
      (selectionItem) => ({
        id: selectionItem.id,
        title: `${selectionItem.offer.price} €`,
        lat: selectionItem.offer.address.latitude,
        lng: selectionItem.offer.address.longitude,
      }),
    ),
  );

  const bounds = computed<GoogleMapLatLngBounds| null>(() => {
    if (!markers.value.length) {
      return null;
    }

    const googleBounds = new google.maps.LatLngBounds();

    markers.value.forEach((marker) => {
      googleBounds.extend({
        lat: marker.lat,
        lng: marker.lng,
      });
    });

    if (savedDealUdurMarker.value) {
      googleBounds.extend({
        lat: savedDealUdurMarker.value.latitude,
        lng: savedDealUdurMarker.value.longitude,
      });
    }

    return googleBounds;
  });

  const center = computed<GoogleMapLatLngLiteral>(() => {
    if (!bounds.value) {
      return {
        lat: 48.8566,
        lng: 2.3522,
      };
    }
    const ne = bounds.value.getNorthEast();
    const sw = bounds.value.getSouthWest();

    return {
      lat: (ne.lat() + sw.lat()) / 2,
      lng: (ne.lng() + sw.lng()) / 2,
    };
  });

  const zoomIn = (): void => {
    if (!map.value) {
      return;
    }

    map.value.setZoom((map.value.getZoom() ?? 0) + 1);
  };

  const zoomOut = (): void => {
    if (!map.value) {
      return;
    }

    map.value.setZoom((map.value.getZoom() ?? 0) - 1);
  };

  const initMap = (newMap: GoogleMap): void => {
    map.value = newMap;

    const transitLayer = new google.maps.TransitLayer();
    transitLayer.setMap(newMap);
  };

  const fitMapToMarkers = (): void => {
    if (!bounds.value || !map.value) {
      return;
    }

    map.value.fitBounds(bounds.value);
  };

  watch([ bounds, map ], () => {
    fitMapToMarkers();
  });

  return {
    markers,
    bounds,
    center,
    zoomIn,
    zoomOut,
    fitMapToMarkers,
    initMap,
  };
});
